import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { css, useTheme } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiStar,
  FiMapPin,
  FiClock,
  FiExternalLink,
  FiUsers,
  FiAlertCircle,
  FiX,
  FiCheck,
  FiCopy,
  FiMail,
} from "react-icons/fi";
import { Spark, StarFilled } from "../components/ui/icons";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import OriginMark from "./OriginMark";
import Skeleton from "./Skeleton";
import { getOutreachState } from "../lib/outreachStateService";
import {
  getCandidateState,
  patchCandidateStatus,
} from "../lib/candidateStateService";
import {
  DEFAULT_STATUS,
  STATUSES,
  statusFor,
  statusLabel,
} from "../lib/pipeline";
import {
  formatLocation,
  formatCountry,
  getLocationValues,
  getCountryValues,
} from "../utils/locationUtils";
import JobDescriptionHtml from "./JobDescriptionHtml";
import CompanyLogo from "./CompanyLogo";
import { AVATAR_TINTS, COMPANY_TINTS, hashStr } from "../theme/ThemeContext";
import { useLockPageScroll } from "../utils/useLockPageScroll";

export { hashStr };

/* Base scope for embedding this view outside the Jobs page Shell. The colour
   tokens come from :root (see theme/ThemeContext) — this only sets the type
   defaults the view assumes. Kept as a component because portalled content
   still needs the base text colour applied outside the page tree. */
export const JobsVarsScope = styled.div`
  color: var(--ink);
  font-size: 14px;
  line-height: 1.45;
`;

/* ─────────────────────────────────────────────────────────
   Shared primitives (used by the Jobs page and this view)
───────────────────────────────────────────────────────── */
export const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease,
    color 0.12s ease, transform 0.06s ease;
  white-space: nowrap;
  &:hover {
    background: var(--surface-tint);
    border-color: var(--line-strong);
  }
  &:active {
    transform: translateY(0.5px);
  }
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* Keep the 32px height set above. The global 44px touch-target floor on
     buttons in App.js clamps it otherwise, which is what leaves buttons
     looking chunky and out of proportion on phones. */
  @media (max-width: 450px) {
    min-height: 0;
  }
  ${({ $primary }) =>
    $primary &&
    css`
      background: var(--accent);
      color: var(--on-fill);
      border-color: var(--accent);
      &:hover {
        background: var(--accent-hover);
        border-color: var(--accent-hover);
      }
    `}
  /* The brand blue, same fill as $primary. */
  ${({ $accent }) =>
    $accent &&
    css`
      background: var(--accent);
      /* --bg, not --on-fill: the accent lightens in dark mode, where a white
         label falls to 2.8:1. Inverting with the surface holds 5.2:1 light and
         6.6:1 dark. */
      color: var(--bg);
      border-color: var(--accent);
      &:hover {
        /* --accent-hover, not --accent-2: the latter is only 3.0:1 in light. */
        background: var(--accent-hover);
        border-color: var(--accent-hover);
      }
    `}
  ${({ $ghost }) =>
    $ghost &&
    css`
      background: transparent;
      border-color: transparent;
      &:hover {
        background: var(--bg-2);
      }
    `}
  ${({ $sm }) =>
    $sm &&
    css`
      height: 28px;
      padding: 0 10px;
      font-size: 12.5px;
      border-radius: 7px;
    `}
  ${({ $lg }) =>
    $lg &&
    css`
      height: 38px;
      padding: 0 16px;
      font-size: 14px;
      border-radius: 10px;
    `}
`;
const BtnLink = (props) => <Btn as="a" {...props} />;

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size || 32}px;
  height: ${({ $size }) => $size || 32}px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-soft);
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
  /* Stay square: the global 44px touch floor stretched it into a tall pill. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

export const Tabs = styled.div`
  display: inline-flex;
  background: var(--bg-2);
  border-radius: 999px;
  padding: 3px;
  margin-bottom: 14px;
  /* Phones: a strip that still runs wide scrolls inside itself instead of
     pushing past the screen edge. */
  @media (max-width: 450px) {
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
export const Tab = styled.button`
  height: 30px;
  padding: 0 16px;
  border: 0;
  background: transparent;
  color: var(--ink-soft);
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &:hover {
    color: var(--ink);
  }
  /* Selected view: a neutral raised pill; colour is kept for actions (blue) and plan limits (orange). */
  ${({ $active }) =>
    $active &&
    css`
      background: var(--surface);
      color: var(--ink);
      box-shadow: 0 1px 3px color-mix(in srgb, #000 14%, transparent), 0 0 0 1px var(--line);
      &:hover {
        color: var(--ink);
      }
    `}
  /* Phones: tighter pills so a three-tab strip fits 360px, and no 44px touch
     floor ballooning the segmented control. */
  @media (max-width: 450px) {
    min-height: 0;
    flex-shrink: 0;
    padding: 0 11px;
    gap: 5px;
    font-size: 12.5px;
  }
`;
export const TabCount = styled.span`
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--bg-2);
  color: var(--ink-soft);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.005em;
  white-space: nowrap;
  ${({ $secondary }) =>
    $secondary &&
    css`
      background: var(--secondary-soft);
      color: var(--secondary);
    `}
  ${({ $success }) =>
    $success &&
    css`
      background: var(--success-soft);
      color: var(--success);
    `}
  ${({ $closed }) =>
    $closed &&
    css`
      background: transparent;
      color: var(--ink-muted);
      border: 1px solid var(--line-strong);
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      font-size: 10.5px;
    `}
`;

/* ─────────────────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────────────────── */
export function jobIdOf(job) {
  if (!job) return "";
  return String(job._id?.$oid || job._id || "");
}
export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return new Date(value);
  if (typeof value === "object" && value.$date) return new Date(value.$date);
  return null;
}
export function postedRel(value) {
  const d = parseDate(value);
  if (!d || isNaN(d)) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
function postedFull(value) {
  const d = parseDate(value);
  if (!d || isNaN(d)) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
export function firstCompany(value) {
  if (!value) return "";
  if (Array.isArray(value)) return String(value[0] || "");
  return String(value);
}
export function initialOf(name) {
  const c = String(name || "").trim();
  return c ? c.charAt(0).toUpperCase() : "?";
}
export function companyColor(name) {
  return COMPANY_TINTS[hashStr(String(name || "")) % COMPANY_TINTS.length];
}
function avatarTint(name) {
  return AVATAR_TINTS[hashStr(String(name || "")) % AVATAR_TINTS.length];
}
function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
}
// Short location for cards: first location only, then " + N others"
// when there's more than one. Country is omitted on the card.
export function locationShort(job) {
  const locs = getLocationValues(job).filter(Boolean);
  if (locs.length === 0) {
    const countries = getCountryValues(job).filter(Boolean);
    return countries[0] || "—";
  }
  const extra = locs.length - 1;
  if (extra <= 0) return locs[0];
  return `${locs[0]} + ${extra} other${extra === 1 ? "" : "s"}`;
}

/* ─────────────────────────────────────────────────────────
   Job detail view
───────────────────────────────────────────────────────── */
const LocPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  min-width: 240px;
  max-width: 320px;
  cursor: default;
  overflow: hidden;
  isolation: isolate;
`;
const LocPopoverScroll = styled.div`
  padding: 10px 12px 12px;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  .lp-head {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-muted);
    font-weight: 600;
    padding: 0 2px 8px;
  }
  .lp-row {
    padding: 5px 2px;
    font-size: 12.5px;
    color: var(--ink-soft);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const DetailWrap = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px 36px 60px;
  @media (max-width: 768px) {
    padding: 16px 18px 40px;
  }
`;
export const JDCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 22px 26px;
  margin-bottom: 14px;
  /* 26px a side left under 290px of text on a 375px phone. */
  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;
const JDHeadTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 18px;
  position: relative;
  /* Keeps the company row and title clear of the save star in the corner. */
  padding-right: 44px;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
`;
/* Save toggle, pinned to the card's top-right corner. A star is the whole
   control here — it was a labelled button in the action row, where it competed
   with the two things you actually come to this page to do. */
const SaveStar = styled.button`
  position: absolute;
  top: -6px;
  right: -8px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  /* Same colours as JCardStar on the jobs list, so the star means one thing
     whether you're looking at a card or the open role. */
  color: ${({ $on }) => ($on ? "var(--star)" : "var(--ink-muted)")};
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover {
    background: var(--bg-2);
    color: ${({ $on }) => ($on ? "var(--star)" : "var(--ink)")};
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;
const JDCompanyRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  margin-bottom: 6px;
  flex-wrap: wrap;
  .name {
    font-weight: 600;
    color: var(--ink-2);
  }
  .type {
    color: var(--ink-muted);
  }
  .sep {
    color: var(--ink-faint);
  }
`;
const JDCode = styled.span`
  font-family: "Geist Mono", ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: var(--ink-muted);
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--bg-2);
  margin-left: 6px;
`;
const JDTitle = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 36px;
  line-height: 1.1;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin: 0;
  font-weight: 400;
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;
const JDMeta = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  font-size: 13px;
  color: var(--ink-muted);
  position: relative;
  .item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    position: relative;
  }
  .item.clickable {
    cursor: pointer;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    transition: background 0.12s, color 0.12s;
  }
  .item.clickable:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
`;
const JDPills = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;
const JDActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 18px;
  border-top: 1px solid var(--line-soft);
`;
// How many matches the reverse match keeps. The server accepts 1-100; these are
// the offered steps, and the default the picker opens on.
const MATCH_COUNT_OPTIONS = [5, 10, 25, 50, 100];
const MATCH_COUNT_DEFAULT = 10;
// Reverse-match polling: a few hundred candidates take ~15 s; the worker's own ceiling is 10 min.
const REVERSE_MATCH_POLL_MS = 2000;
const REVERSE_MATCH_MAX_MS = 10 * 60 * 1000;
// "Top N" picker sitting beside the match buttons — how many candidates the
// reverse match keeps.
const MatchCountSelect = styled.select`
  height: 30px;
  padding: 0 6px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  /* Match the 30px buttons beside it rather than the global 44px floor. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
// Shown at the top of a closed posting opened from saved jobs.
const JDClosedBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: var(--bg-2);
  border: 1px dashed var(--line-strong);
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 14px;
  color: var(--ink-soft);
  font-size: 13px;
  line-height: 1.5;
  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--ink-muted);
  }
  b {
    color: var(--ink);
    font-weight: 600;
  }
`;
const JDGlanceStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count || 6}, 1fr);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 14px;
  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    row-gap: 12px;
  }
  /* Three columns on a phone left ~75px per value; function and company
     names broke over three lines. */
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 14px 16px;
  }
`;
const JDGlanceCell = styled.div`
  padding: 0 12px;
  border-right: 1px solid var(--line-soft);
  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    border-right: 0;
  }
  @media (max-width: 900px) {
    &:nth-child(3n) {
      border-right: 0;
    }
  }
  /* Two-column rhythm: left cells flush, right cells unruled. */
  @media (max-width: 480px) {
    min-width: 0;
    overflow-wrap: anywhere;
    &:nth-child(3n) {
      border-right: 1px solid var(--line-soft);
    }
    &:nth-child(2n) {
      border-right: 0;
    }
    &:nth-child(2n + 1) {
      padding-left: 0;
    }
    &:last-child {
      border-right: 0;
    }
  }
`;
const JDGlanceLbl = styled.div`
  font-size: 10.5px;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  margin-bottom: 5px;
`;
const JDGlanceVal = styled.div`
  font-size: 14px;
  color: var(--ink);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

const JDSectionH = styled.h3`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 22px;
  line-height: 1.1;
  color: var(--ink);
  margin: 0 0 10px;
  letter-spacing: -0.005em;
  font-weight: 400;
`;
const JDDescription = styled(JDCard)`
  padding: 26px 32px;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ink);
  @media (max-width: 600px) {
    padding: 20px 16px;
  }
  & p {
    margin: 0 0 12px;
  }
  & h1, & h2, & h3, & h4 {
    font-family: "Instrument Serif", Georgia, serif;
    color: var(--ink);
    margin: 18px 0 10px;
    font-weight: 400;
    letter-spacing: -0.005em;
  }
  & h1 { font-size: 24px; }
  & h2 { font-size: 22px; }
  & h3 { font-size: 19px; }
  & h4 { font-size: 16px; }
  & ul, & ol {
    margin: 8px 0 14px;
    padding-left: 22px;
  }
  & li {
    margin: 4px 0;
  }
  & a {
    color: var(--accent);
    text-decoration: underline;
  }
  & strong, & b {
    color: var(--ink);
    font-weight: 600;
  }
`;
const JDContactsHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  .sub {
    font-size: 12.5px;
    color: var(--ink-muted);
    margin-top: 4px;
  }
`;
export const AiPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, var(--accent-soft), var(--surface));
  color: var(--accent-ink);
  border: 1px solid var(--accent-soft);
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 500;
`;
/* Empty state — mirrors the Dashboard's .empty-state design (gradient icon
   tile, serif display title, muted copy, CTA) rebuilt on the Jobs palette so
   the two pages read as one system. Instrument Serif loads globally in
   public/index.html. */
const JDEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 20px 26px;

  .empty-ico {
    width: 54px;
    height: 54px;
    border-radius: 15px;
    display: grid;
    place-items: center;
    color: var(--accent);
    background: linear-gradient(155deg, var(--accent-soft), var(--surface-tint));
    border: 1px solid var(--line);
    margin-bottom: 14px;
    /* Tinted from the live accent so it tracks light/dark; the flat rgba is
       the fallback for engines without color-mix. */
    box-shadow: var(--shadow-md);
    box-shadow: 0 6px 16px -8px color-mix(in srgb, var(--accent) 35%, transparent);
  }
  .empty-title {
    font-family: "Instrument Serif", "New York", Georgia, serif;
    font-size: 20px;
    line-height: 1.15;
    color: var(--ink);
    letter-spacing: -0.005em;
  }
  .empty-text {
    font-size: 12.5px;
    color: var(--ink-muted);
    max-width: 280px;
    line-height: 1.5;
    margin-top: 5px;
  }
  .empty-cta {
    margin-top: 15px;
  }
`;

/* ── Shortlist ────────────────────────────────────────────────────────────
   A shortlist is a pipeline, not a gallery: the question it answers is "who is
   where", so the rows are grouped under the stage they're at, furthest along
   first, and each group shares a rail in that stage's colour. The grouping is
   the structure — no status pill per row, because the heading above already
   said it. Rejected sits last: an exit, not progress.
─────────────────────────────────────────────────────────────────────────── */
const StageGroup = styled.section`
  & + & {
    margin-top: 20px;
  }
  .g-h {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 4px 14px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 650;
    color: var(--ink-muted);
  }
  .g-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.$rail};
    flex-shrink: 0;
  }
  .g-count {
    color: var(--ink-3, var(--ink-muted));
    font-variant-numeric: tabular-nums;
  }
  .g-rule {
    flex: 1;
    height: 1px;
    background: var(--line-soft);
  }
`;
const PersonRow = styled.div`
  position: relative;
  display: grid;
  grid-template-areas: "av who where score";
  grid-template-columns: auto minmax(0, 1.05fr) minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 14px;
  padding: 9px 12px 9px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s;
  /* The stage rail. Runs down every row in the group, so a stage reads as one
     block from across the page — the one piece of colour the list carries. */
  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 5px;
    bottom: 5px;
    width: 2px;
    border-radius: 2px;
    background: ${(p) => p.$rail};
    opacity: 0.5;
    transition: opacity 0.12s;
  }
  &:hover {
    background: var(--surface-tint);
  }
  &:hover::before {
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
  .r-av {
    grid-area: av;
    align-self: center;
  }
  /* Two facts per cell, on a shared baseline across the row: who they are on
     the left (name over title), where they are on the right (company over
     location). Both columns line up down the whole list, which is what makes
     it scannable — the previous "Title · Company" ran the two together and
     started every row at a different place. */
  .r-who,
  .r-where {
    min-width: 0;
  }
  .r-who {
    grid-area: who;
  }
  .r-where {
    grid-area: where;
  }
  /* min-width: 0 is what actually lets these ellipsise inside a grid cell. */
  .l-1,
  .l-2 {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .l-1 {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.3;
  }
  .r-where .l-1 {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--ink-2);
  }
  .l-2 {
    font-size: 12px;
    color: var(--ink-muted);
    line-height: 1.35;
    margin-top: 1px;
  }
  .l-1 svg,
  .l-2 svg {
    vertical-align: -2px;
    margin-right: 4px;
  }
  /* The name line also carries the origin mark, so it's a flex row. */
  .r-who .l-1 {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: visible;
  }
  .r-who .l-1 .n-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .r-score {
    grid-area: score;
    justify-self: end;
    font-size: 12.5px;
    font-weight: 650;
    color: var(--accent-ink);
    font-variant-numeric: tabular-nums;
  }
  .r-score.is-none {
    color: var(--ink-3, var(--ink-muted));
    font-weight: 500;
  }
  /* Contacts have no score; the same cell carries whether we hold an address,
     which is the one thing that decides if you can act on them. */
  .r-mail {
    grid-area: score;
    justify-self: end;
    display: inline-flex;
    align-items: center;
    color: var(--ink-3, var(--ink-muted));
  }
  /* Narrow: the where-column drops under the who-column rather than crushing
     both to a few characters. */
  @media (max-width: 680px) {
    grid-template-areas:
      "av who score"
      "av where where";
    grid-template-columns: auto minmax(0, 1fr) auto;
    row-gap: 4px;
  }
`;

export const Avatar = styled.span`
  width: ${({ $size }) => $size || 44}px;
  height: ${({ $size }) => $size || 44}px;
  border-radius: 999px;
  display: inline-grid;
  place-items: center;
  font-size: ${({ $size }) => ($size && $size < 40 ? 12 : 14)}px;
  font-weight: 600;
  letter-spacing: 0.02em;
  /* Cards stack the avatar above the name; rows sit it beside them. */
  margin-bottom: ${({ $size }) => ($size ? 0 : 8)}px;
  flex-shrink: 0;
  overflow: hidden;
  background: ${({ $bg }) => $bg || "var(--bg-2)"};
  color: ${({ $fg }) => $fg || "var(--ink-soft)"};
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

/* Avatar that shows a real photo (thumbnail_src) when available, falling back
   to tinted initials if there's no image or it fails to load. */
function ContactAvatar({ src, name, tint, size }) {
  const [broken, setBroken] = useState(false);
  return (
    <Avatar $bg={tint.bg} $fg={tint.fg} $size={size}>
      {src && !broken ? (
        <AvatarImg src={src} alt={name} onError={() => setBroken(true)} />
      ) : (
        initialsOf(name) || "?"
      )}
    </Avatar>
  );
}

/* ─────────────────────────────────────────────────────────
   AI matches picker — the ranked pool for this role, opened
   on demand from the Candidates tab instead of sitting on the
   page. Everything picked here lands on the job's shortlist.
───────────────────────────────────────────────────────── */
/* Extends JobsVarsScope: portalled to <body>, the overlay sits outside the page
   tree and has to re-supply the Jobs tokens its children resolve against. */
const MatchOverlay = styled(JobsVarsScope)`
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  /* Above any host overlay (10000) that embeds this view. */
  z-index: 10500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  @media (max-width: 768px) {
    padding: 12px;
  }
`;
const MatchModal = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
  /* Overlay padding drops to 12px here; reclaim the height it frees. */
  @media (max-width: 768px) {
    max-height: calc(100dvh - 24px);
  }
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const MatchHead = styled.div`
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--line-soft);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  .h-title {
    font-family: "Instrument Serif", "New York", Georgia, serif;
    font-size: 22px;
    line-height: 1.15;
    color: var(--ink);
  }
  .h-sub {
    font-size: 12.5px;
    color: var(--ink-muted);
    margin-top: 3px;
  }
  .h-tools {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
/* Select-all strip between the header and the list. */
const MatchToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 20px;
  border-bottom: 1px solid var(--line-soft);
  background: var(--surface-tint);
  font-size: 12.5px;
  color: var(--ink-soft);
  .count {
    color: var(--ink-muted);
  }
`;
const MatchBody = styled.div`
  padding: 8px;
  overflow-y: auto;
  /* min-height: 0 lets the flex child shrink so this scrolls instead of
     stretching the modal past the viewport. */
  flex: 1 1 auto;
  min-height: 0;
`;
const MatchFoot = styled.div`
  padding: 12px 20px;
  border-top: 1px solid var(--line-soft);
  background: var(--surface-tint);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  .picked {
    font-size: 12.5px;
    color: var(--ink-muted);
  }
  .err {
    font-size: 12.5px;
    color: var(--danger);
  }
`;
const MatchCheckbox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: var(--accent);
  cursor: pointer;
  /* The global 44px input floor made each picker row ~60px tall. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const MatchRow = styled.label`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 11px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: var(--surface-tint);
  }
  ${({ $selected }) =>
    $selected &&
    css`
      background: var(--accent-soft);
      border-color: var(--accent-soft);
      &:hover {
        background: var(--accent-soft);
      }
    `}
  /* Already on the job. Green rather than the accent so "done" never reads as
     "picked", and a left rule so the state survives a quick scan. */
  ${({ $added }) =>
    $added &&
    css`
      background: var(--success-soft);
      border-color: var(--success-soft);
      box-shadow: inset 3px 0 0 var(--success);
      cursor: default;
      &:hover {
        background: var(--success-soft);
      }
    `}
  .row-added {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--success);
    background: var(--bg);
    padding: 1px 6px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .who {
    flex: 1;
    min-width: 0;
  }
  .row-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 6px;
    /* Clip the name, not every span on the line: OriginMark's root is a span
       too, and hiding its overflow swallowed the hover label it exists for. */
    .n-text {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .row-meta {
    font-size: 11.5px;
    color: var(--ink-muted);
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  /* Title · location on one line — the status chip beside it never shrinks. */
  .row-sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-status {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--ink-soft);
    background: var(--bg-2);
    padding: 1px 6px;
    border-radius: 4px;
  }
  /* Fixed column, left-aligned: 9% and 100% then start at the same edge, so
     the figures read as a scannable column instead of a ragged one. */
  .row-score {
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--accent);
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    flex-shrink: 0;
    width: 62px;
  }
`;

/** Stable id for a candidate across the match / chosen shapes. */
const candidateKey = (c) => String(c?._id?.$oid || c?._id || c?.id || "");

/* One colour per stage, grouped like the Outreach board: target = neutral,
   outreach through interviewing = in flight, offer/placement = won, rejected =
   lost. Shared by the shortlist rails and the profile modal's stage control so
   a stage looks the same everywhere on the page. */
function stageColor(status) {
  if (status === "rejected") return "var(--danger)";
  if (status === "offer" || status === "placement") return "var(--success)";
  if (!status || status === DEFAULT_STATUS) return "var(--ink-3, var(--ink-muted))";
  return "var(--accent)";
}

/* ─────────────────────────────────────────────────────────
   Candidate profile, in place.

   Clicking a shortlist tile used to navigate to Outreach with ?personId=,
   which boots that whole page — candidates, clients, mail history, outreach
   state — to show one person. The shortlist rows are already fully hydrated
   (summary, experience, education, contact), so this renders from what's in
   hand: no request, no route change, and the job stays behind it. Outreach is
   still one click away for the things that live there — composing, timeline.
───────────────────────────────────────────────────────── */
/* Header band: the person is the title here, so the generic modal head is
   replaced by their own — avatar, name in the display serif, and the facts
   that qualify them, in one descending weight. */
const ProfileHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--line-soft);
  background: linear-gradient(160deg, var(--surface-tint), var(--surface) 70%);
  .p-name {
    font-family: "Instrument Serif", "New York", Georgia, serif;
    font-size: 24px;
    line-height: 1.1;
    color: var(--ink);
  }
  .p-role {
    font-size: 13.5px;
    color: var(--ink-soft);
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
/* Qualifying facts under the name. Chips, not a run-on line: each is a
   different kind of fact and they wrap independently on a narrow modal. */
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 9px;
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    border: 1px solid var(--line);
    background: var(--surface);
    font-size: 11.5px;
    color: var(--ink-muted);
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
  }
  /* The origin mark's tooltip escapes upward, so nothing on this path may
     clip: no overflow, no fixed height. */
  .mark {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 2px;
    overflow: visible;
    color: var(--ink-muted);
  }
  .chip.accent {
    border-color: transparent;
    background: var(--accent-soft);
    color: var(--accent-ink);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;
/* Stage + contact, the two things you act on, pinned directly under the header
   so they never scroll out of reach on a long profile. */
const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 11px 22px;
  border-bottom: 1px solid var(--line-soft);
  background: var(--surface-tint);
  .a-label {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    font-weight: 650;
    color: var(--ink-muted);
    margin-bottom: 5px;
  }
  .a-spacer {
    flex: 1;
  }
  .a-contact {
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
  }
`;
/* Stage control for this job. A plain select, like the Outreach per-job rows —
   the job is fixed by context here, so there's nothing to disambiguate. The
   label takes the stage's colour, tying it to the pills on the tiles. */
const ProfileStatusSelect = styled.select`
  height: 30px;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: ${(p) => p.$fg || "var(--ink)"};
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  &:hover {
    border-color: var(--line-strong);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  &[disabled] {
    opacity: 0.6;
    cursor: default;
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
  option {
    color: var(--ink);
    background: var(--surface);
  }
`;
/* Body sections share one rhythm: a ruled label, then content. The rule keeps
   long profiles scannable without boxing every block in its own card. */
const PSection = styled.section`
  & + & {
    margin-top: 20px;
  }
  .s-h {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    font-weight: 650;
    color: var(--ink-muted);
    margin-bottom: 10px;
  }
  .s-h::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--line-soft);
  }
  .s-body {
    font-size: 13.5px;
    line-height: 1.62;
    color: var(--ink-2);
    white-space: pre-wrap;
  }
`;
/* History rows hang off a hairline rail with a node per entry — reads as one
   run of time rather than a stack of separate cards. */
const TimeRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: baseline;
  padding: 0 0 14px 18px;
  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 6px;
    bottom: 0;
    width: 1px;
    background: var(--line);
  }
  &:last-child::before {
    bottom: auto;
    height: 0;
  }
  &::after {
    content: "";
    position: absolute;
    left: 1px;
    top: 4px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => (p.$on ? "var(--accent)" : "var(--line-strong)")};
  }
  .r-title {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
  }
  .r-sub {
    font-size: 12.5px;
    color: var(--ink-muted);
    margin-top: 2px;
  }
  .r-when {
    font-size: 11.5px;
    color: var(--ink-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

function PersonProfileModal({
  person,
  kind = "candidate",
  jobTitle,
  companyName,
  status,
  onChangeStatus,
  onOpenInOutreach,
  onClose,
}) {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  // Filled in for rows that arrive thin. Saved HR contacts are snapshots from
  // the outreach blob (snapshotPerson keeps a dozen fields so the blob stays
  // inside its 1MB cap), so they have no summary or history — the rest is
  // fetched here, on open, for the one person being looked at.
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const personId = candidateKey(person);
  const isThin =
    !!person &&
    !person.summary &&
    !person.og_description &&
    !(person.experience || []).length &&
    !(person.education || []).length;

  useEffect(() => {
    setDetail(null);
    if (!personId || !isThin) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchWithAuth(`/api/people/${encodeURIComponent(personId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        setDetail(d && typeof d === "object" ? d : null);
        setDetailLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [personId, isThin]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useLockPageScroll();

  if (!person) return null;

  // A contact is an HR person at the company — no pipeline stage, no match
  // score; they aren't being moved through anything.
  const isCandidate = kind === "candidate";
  const name = person.name || person.full_name || "—";
  const role = person.position || person.role || person.title || "";
  const company = person.company || person.org || companyName || "";
  const loc = formatLocation(person) || formatCountry(person) || "";
  const linkedin = person.link || person.linkedin || "";
  // Narrow merge, not a spread: the snapshot's name/role/company are what the
  // user saved and recognises, so the fetched document only fills the gaps.
  const email =
    person.email || person.probable_email || detail?.email || detail?.probable_email || "";
  const summary =
    person.summary ||
    person.og_description ||
    detail?.summary ||
    detail?.og_description ||
    "";
  const experience = Array.isArray(person.experience) && person.experience.length
    ? person.experience
    : Array.isArray(detail?.experience)
      ? detail.experience
      : [];
  const education = Array.isArray(person.education) && person.education.length
    ? person.education
    : Array.isArray(detail?.education)
      ? detail.education
      : [];
  const origin =
    person.origin === "external" || person.origin === "internal"
      ? person.origin
      : null;
  const score =
    isCandidate && typeof person.similarity_score === "number"
      ? Math.round(person.similarity_score * 100)
      : null;
  const addedVia =
    person.chosenSource === "search"
      ? "From a search on this role"
      : person.chosenSource === "match"
        ? "From AI matches"
        : null;
  const setStatus = async (next) => {
    setSaving(true);
    try {
      await onChangeStatus(next);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <MatchOverlay
      $dark={theme?.mode === "dark"}
      onClick={onClose}
      role="presentation"
    >
      <MatchModal
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${name} — ${isCandidate ? "candidate" : "contact"} profile`}
      >
        <ProfileHead>
          <ContactAvatar
            src={person.thumbnail_src || person.photo || ""}
            name={name}
            tint={avatarTint(name)}
            size={54}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="p-name">{name}</div>
            {(role || company) && (
              <div className="p-role" title={[role, company].filter(Boolean).join(" · ")}>
                {[role, company].filter(Boolean).join(" · ")}
              </div>
            )}
            <MetaRow>
              {loc && (
                <span className="chip">
                  <FiMapPin size={11} /> {loc}
                </span>
              )}
              {score !== null && (
                <span className="chip accent">
                  <Spark size={11} /> {score}% match
                </span>
              )}
              {/* Bare, not in a chip: the mark carries its own hover label,
                  and the chip's overflow clipped it. */}
              {origin && (
                <span className="mark">
                  <OriginMark origin={origin} />
                </span>
              )}
              {addedVia && <span className="chip">{addedVia}</span>}
            </MetaRow>
          </div>
          <IconBtn type="button" onClick={onClose} aria-label="Close">
            <FiX size={15} />
          </IconBtn>
        </ProfileHead>

        <ActionBar>
          {isCandidate ? (
            <div>
              <div className="a-label">Stage on this role</div>
              <ProfileStatusSelect
                value={status}
                $fg={stageColor(status)}
                disabled={saving}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Pipeline stage for this job"
              >
                {STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </ProfileStatusSelect>
            </div>
          ) : (
            <div className="a-label" style={{ marginBottom: 0 }}>
              HR contact{company ? ` at ${company}` : ""}
            </div>
          )}
          <span className="a-spacer" />
          {(email || linkedin) && (
            <div className="a-contact">
              {email && (
                <Btn
                  $sm
                  onClick={() => {
                    navigator.clipboard?.writeText(email);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  title={`Copy ${email}`}
                >
                  {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}{" "}
                  {copied ? "Copied" : "Email"}
                </Btn>
              )}
              {linkedin && (
                <Btn
                  as="a"
                  $sm
                  href={
                    linkedin.startsWith("http") ? linkedin : `https://${linkedin}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink size={12} /> LinkedIn
                </Btn>
              )}
            </div>
          )}
        </ActionBar>

        <MatchBody style={{ padding: "18px 22px 20px" }}>
          {summary && (
            <PSection>
              <div className="s-h">About</div>
              <div className="s-body">{summary}</div>
            </PSection>
          )}

          {experience.length > 0 && (
            <PSection>
              <div className="s-h">Experience</div>
              {experience.slice(0, 6).map((e, i) => (
                <TimeRow key={i} $on={!!e.current}>
                  <div style={{ minWidth: 0 }}>
                    <div className="r-title">{e.title || "—"}</div>
                    <div className="r-sub">
                      {[e.company, e.location].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {e.durationText && <div className="r-when">{e.durationText}</div>}
                </TimeRow>
              ))}
            </PSection>
          )}

          {education.length > 0 && (
            <PSection>
              <div className="s-h">Education</div>
              {education.slice(0, 4).map((ed, i) => (
                <TimeRow key={i}>
                  <div style={{ minWidth: 0 }}>
                    <div className="r-title">{ed.school || "—"}</div>
                    <div className="r-sub">
                      {[ed.degree, ed.details].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {(ed.startDate || ed.endDate) && (
                    <div className="r-when">
                      {[ed.startDate, ed.endDate].filter(Boolean).join(" – ")}
                    </div>
                  )}
                </TimeRow>
              ))}
            </PSection>
          )}

          {/* Placeholders while the rest is fetched, shaped like the About
              block that usually lands — so the modal doesn't say "nothing
              here" and then contradict itself half a second later. */}
          {detailLoading && !summary && experience.length === 0 && (
            <PSection>
              <div className="s-h">About</div>
              <Skeleton height={13} mb={9} />
              <Skeleton height={13} mb={9} />
              <Skeleton height={13} width="62%" />
            </PSection>
          )}

          {!detailLoading &&
            !summary &&
            experience.length === 0 &&
            education.length === 0 && (
              <div
                style={{
                  padding: "22px 0",
                  textAlign: "center",
                  fontSize: 13,
                  color: "var(--ink-muted)",
                }}
              >
                No profile detail on file for this{" "}
                {isCandidate ? "candidate" : "contact"}.
              </div>
            )}
        </MatchBody>

        <MatchFoot>
          <span className="picked">Compose and timeline live in Outreach</span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn $sm onClick={onClose}>
              Close
            </Btn>
            <Btn $sm $primary onClick={onOpenInOutreach}>
              Open in Outreach <FiExternalLink size={12} />
            </Btn>
          </div>
        </MatchFoot>
      </MatchModal>
    </MatchOverlay>,
    document.body,
  );
}

/* One-off picker over the ranked matches. Portalled to <body> so a host that
   scrolls or animates the detail view (a preview modal) can't clip it;
   JobsVarsScope re-supplies the Jobs tokens outside the page tree. */
function AiMatchesModal({
  onClose,
  jobId,
  candidates,
  loading,
  matching,
  matchCountPicker,
  onMatch,
  canMatch,
  statusMap,
  chosenKeys,
  onAdd,
}) {
  const theme = useTheme();
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  // Optimistic tags, so a row turns green the instant it's added rather than
  // waiting on the refetch. chosenKeys is the real answer and takes over once
  // it lands — which is also what keeps rows green across a re-match.
  const [justAdded, setJustAdded] = useState(() => new Set());
  const rows = candidates;
  const isAdded = useCallback(
    (key) => !!key && (chosenKeys.has(key) || justAdded.has(key)),
    [chosenKeys, justAdded],
  );

  // Drop anyone from the selection who is no longer in the list, or who is
  // now on the job — a re-match can change both.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(rows.map(candidateKey));
      const next = new Set(
        [...prev].filter((k) => live.has(k) && !chosenKeys.has(k)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [rows, chosenKeys]);

  // Backdrop, Escape and the X all close — except mid-add, where dismissing
  // would leave the user unsure whether their picks landed.
  const requestClose = useCallback(() => {
    if (!adding) onClose();
  }, [adding, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  useLockPageScroll();

  const toggle = (key) => {
    if (!key) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Rows with an id that aren't already on the job — what "all" can mean.
  const selectableKeys = useMemo(
    () => rows.map(candidateKey).filter((k) => k && !isAdded(k)),
    [rows, isAdded],
  );
  // Counted off the visible rows, not the whole job — this is "how many of
  // these are already on it", which is what the row tags are saying.
  const addedCount = useMemo(
    () => rows.filter((c) => isAdded(candidateKey(c))).length,
    [rows, isAdded],
  );
  const allSelected =
    selectableKeys.length > 0 && selected.size === selectableKeys.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableKeys));

  const addSelected = async () => {
    const picks = rows.filter((c) => selected.has(candidateKey(c)));
    if (picks.length === 0) return;
    setAdding(true);
    setError("");
    try {
      await onAdd(picks);
      // Stay open and mark them instead of closing: the picker is easier to
      // work through in passes when you can see what you've already taken.
      setJustAdded((prev) => new Set([...prev, ...picks.map(candidateKey)]));
      setSelected(new Set());
    } catch {
      setError("Couldn't add those candidates. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return createPortal(
    <MatchOverlay
      $dark={theme?.mode === "dark"}
      onClick={requestClose}
      role="presentation"
    >
      <MatchModal
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Match candidates"
      >
        <MatchHead>
          <div>
            <div className="h-title">Match candidates</div>
            <div className="h-sub">
              {loading || matching
                ? "Ranking your candidates against this role…"
                : rows.length > 0
                  ? `${rows.length} ranked by fit — pick who joins this role`
                  : "No matches yet for this role"}
            </div>
          </div>
          <div className="h-tools">
            {/* Only once there's a list to re-run. With none, the empty state
                below already offers the picker and the button. */}
            {canMatch && rows.length > 0 && (
              <>
                {matchCountPicker}
                <Btn $sm onClick={onMatch} disabled={matching}>
                  <Spark size={12} /> {matching ? "Matching…" : "Match again"}
                </Btn>
              </>
            )}
            <IconBtn
              type="button"
              $size={30}
              onClick={requestClose}
              disabled={adding}
              title="Close"
              aria-label="Close"
            >
              <FiX size={15} />
            </IconBtn>
          </div>
        </MatchHead>

        {rows.length > 0 && (
          <MatchToolbar>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <MatchCheckbox
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
              />
              Select all
            </label>
            <span className="count">
              {addedCount > 0 && `${addedCount} added · `}
              {selected.size} of {selectableKeys.length} selected
            </span>
          </MatchToolbar>
        )}

        <MatchBody>
          {rows.length === 0 && (
            <JDEmpty>
              <div className="empty-ico">
                <Spark size={24} />
              </div>
              <div className="empty-title">
                {loading || matching
                  ? "Scanning your talent pool…"
                  : "Let AI surface your best fits"}
              </div>
              <div className="empty-text">
                {loading || matching
                  ? "Ranking every candidate against this role. Takes about a minute."
                  : "We'll scan your talent pool against this role and rank candidates by fit, then you pick who joins it."}
              </div>
              {canMatch && !loading && !matching && (
                <div
                  className="empty-cta"
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {matchCountPicker}
                  <Btn $primary onClick={onMatch}>
                    <Spark size={14} /> Match now
                  </Btn>
                </div>
              )}
            </JDEmpty>
          )}
          {rows.map((c, i) => {
            const key = candidateKey(c);
            const name = c.name || c.full_name || c.title || "—";
            const titleText = c.position || c.role || c.title || "";
            const cLoc = formatLocation(c) || formatCountry(c) || "";
            const status = statusFor(statusMap, jobId, key);
            const origin =
              c.origin === "external" || c.origin === "internal"
                ? c.origin
                : null;
            const score =
              typeof c.similarity_score === "number"
                ? Math.round(c.similarity_score * 100)
                : null;
            const added = isAdded(key);
            const on = selected.has(key);
            return (
              <MatchRow
                key={key || `row-${i}`}
                $selected={on && !added}
                $added={added}
              >
                <MatchCheckbox
                  type="checkbox"
                  checked={on || added}
                  /* No id means nothing to attach — the server would drop it.
                     Already-added rows lock so they can't be sent twice. */
                  disabled={!key || added}
                  onChange={() => toggle(key)}
                />
                <ContactAvatar
                  src={c.thumbnail_src || c.photo || ""}
                  name={name}
                  tint={avatarTint(name)}
                  size={34}
                />
                <div className="who">
                  <div className="row-name">
                    <span className="n-text">{name}</span>
                    {origin && <OriginMark origin={origin} />}
                    {added && (
                      <span className="row-added">
                        <FiCheck size={10} /> Added
                      </span>
                    )}
                  </div>
                  <div className="row-meta">
                    <span className="row-sub">
                      {[titleText, cLoc].filter(Boolean).join(" · ")}
                    </span>
                    {/* Only a status they've actually moved to — everyone
                        starts on the default, so showing it labels every row
                        with the same word. */}
                    {status !== DEFAULT_STATUS && (
                      <span className="row-status">{statusLabel(status)}</span>
                    )}
                  </div>
                </div>
                {score !== null && (
                  <span className="row-score">
                    <Spark size={12} /> {score}%
                  </span>
                )}
              </MatchRow>
            );
          })}
        </MatchBody>

        {/* Nothing to act on with an empty list — the whole bar would just be
            disabled controls, so it stays out until there are rows to pick. */}
        {rows.length > 0 && (
          <MatchFoot>
            <span className={error ? "err" : "picked"}>
              {error ||
                (selected.size > 0
                  ? `${selected.size} candidate${selected.size === 1 ? "" : "s"} ready to add`
                  : addedCount > 0
                    ? `${addedCount} of these already on this role`
                    : "Nobody selected yet")}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              {/* Reads as Done once something has landed — there's nothing to
                  cancel at that point, the adds are already saved. */}
              <Btn onClick={onClose} disabled={adding}>
                {addedCount > 0 && selected.size === 0 ? "Done" : "Cancel"}
              </Btn>
              <Btn
                $primary
                onClick={addSelected}
                disabled={selected.size === 0 || adding}
              >
                <FiUsers size={13} />{" "}
                {adding ? "Adding…" : "Add to shortlist"}
              </Btn>
            </div>
          </MatchFoot>
        )}
      </MatchModal>
    </MatchOverlay>,
    document.body,
  );
}

/* Full job detail. onToggleSave / onSourceCandidates are optional —
   hosts that embed the view (e.g. the wizard's preview modal) omit them
   and the corresponding actions are hidden. */
export default function JobDetailView({
  job,
  saved,
  onBack,
  onToggleSave,
  onSourceCandidates,
  backLabel = "jobs",
  /* The posting as a read-only reference: the description tab alone, no matching. */
  descriptionOnly = false,
}) {
  const navigate = useNavigate();
  const { user, isAuthenticated, features: featureFlags } = useAuth();
  // Job matching is parked (FEATURE_JOB_MATCH): no Match candidates button or dialog while it is off.
  const jobMatchOn = !!featureFlags?.jobMatch;
  const company = firstCompany(job.source);
  const title = job.title || job.job_title || "Untitled role";
  const id = jobIdOf(job);
  const url =
    job.link || job.url || job.career_url || job.application_url || "";
  const country = formatCountry(job);
  const loc = locationShort(job);
  const rel = postedRel(job.posted_date);
  const full = postedFull(job.posted_date);
  const jobFunction = job.function || "";
  const seniority = job.seniority || "";
  const allLocs = useMemo(
    () => getLocationValues(job).filter(Boolean),
    [job],
  );
  const hasMultiLoc = allLocs.length > 1;
  const [showLocs, setShowLocs] = useState(false);
  const locItemRef = useRef(null);
  useEffect(() => {
    if (!showLocs) return;
    const close = (e) => {
      if (locItemRef.current && !locItemRef.current.contains(e.target)) {
        setShowLocs(false);
      }
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [showLocs]);

  // HR contacts (lazy fetch from /api/people)
  const [hrContacts, setHrContacts] = useState([]);
  const [hrLoading, setHrLoading] = useState(false);
  // Contact search: "idle" | "queued" (worker running, we watch its status) |
  // "empty" (finished, found nobody) | an error/notice string.
  const [hrFindState, setHrFindState] = useState("idle");
  // True once the search (and therefore the list) drops the job's country scope.
  const [hrWorldwide, setHrWorldwide] = useState(false);
  // Bumped to silently re-run the fetch below (no loading flicker).
  const [hrRefresh, setHrRefresh] = useState(0);
  useEffect(() => {
    setHrFindState("idle");
    setHrWorldwide(false);
    setHrRefresh(0);
    if (!company) return undefined;
    // A search for this company may already be running (started on an earlier
    // visit or another tab): resume watching it instead of offering a duplicate
    // run — every run spends credits.
    let cancelled = false;
    fetchWithAuth(`/api/research/hr-status?company=${encodeURIComponent(company)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.state === "running") setHrFindState("queued");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [company, id]);
  useEffect(() => {
    if (!company) {
      setHrContacts([]);
      return;
    }
    if (hrRefresh === 0) setHrLoading(true);
    const params = new URLSearchParams();
    params.append("company", company);
    params.append("position_category", "HR");
    params.append("limit", "8");
    const countries = getCountryValues(job);
    if (countries.length > 0 && !hrWorldwide)
      params.append("country", countries.join(","));
    let cancelled = false;
    fetchWithAuth(`/api/people?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : { people: [] }))
      .then((data) => {
        if (cancelled) return;
        const people = Array.isArray(data?.people) ? data.people : [];
        setHrContacts(people);
        setHrLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHrContacts([]);
        setHrLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [company, id, hrRefresh, hrWorldwide]);

  // While a search runs, watch its actual status instead of guessing from the
  // people list — a fast "found nobody" run must read as finished, not pending.
  useEffect(() => {
    if (hrFindState !== "queued" || !company) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    const check = async () => {
      try {
        const r = await fetchWithAuth(
          `/api/research/hr-status?company=${encodeURIComponent(company)}`,
        );
        const data = r.ok ? await r.json() : null;
        if (cancelled || !data) return;
        if (data.state === "done") {
          if ((data.found || 0) > 0) {
            setHrFindState("idle");
            setHrRefresh((n) => n + 1);
          } else {
            setHrFindState("empty");
          }
        } else if (data.state === "failed") {
          setHrFindState("The contact search failed — please try again.");
        } else if (Date.now() - startedAt > 10 * 60 * 1000) {
          setHrFindState(
            "Still searching — contacts will appear here once mapped. Check back in a few minutes.",
          );
        }
        // "running" / "none": keep waiting.
      } catch {
        // transient — next tick retries
      }
    };
    check();
    const timer = setInterval(check, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [hrFindState, company]);

  // "Find HR contacts": queue the on-demand mapper for this company, scoped to the
  // job's country — or worldwide on the empty-result retry. A 409 means a search is
  // already running, and watching it is the right response either way.
  const startHrFind = useCallback(
    async (worldwide = false) => {
      if (!company) return;
      setHrWorldwide(worldwide);
      setHrFindState("queued");
      try {
        const r = await fetchWithAuth("/api/research/find-hr", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyName: company,
            country: worldwide ? "" : getCountryValues(job)[0] || "",
          }),
        });
        if (!r.ok && r.status !== 409) {
          const data = await r.json().catch(() => ({}));
          setHrFindState(data?.error || "Couldn't start the contact search.");
        }
      } catch {
        setHrFindState("Couldn't start the contact search.");
      }
    },
    [company, id],
  );

  // Matched candidates — the user's own candidates ranked against this job.
  // Rendered with the same card format as HR contacts. Requires auth; the
  // server decrypts per-tenant, so on the no-KMS dev box this returns empty,
  // which we render as the "no matches yet" state.
  const [matchedCandidates, setMatchedCandidates] = useState([]);
  // The job's shortlist — candidates deliberately attached to it, either assigned by
  // hand from the candidate detail panel or shortlisted to outreach from a
  // search run against this job. Separate concern from the AI matches above.
  const [chosenCandidates, setChosenCandidates] = useState([]);
  // A person row clicked → their profile, over the job: { person, kind }.
  // Holds the row itself, which already carries everything the modal shows, so
  // opening costs nothing. `kind` is "candidate" (has a stage on this role) or
  // "contact" (HR at the company — no pipeline).
  const [profilePerson, setProfilePerson] = useState(null);
  const [candLoading, setCandLoading] = useState(false);
  // Pipeline status per candidate id, from candidate_state. Same map the
  // Outreach page drives its status chips from.
  const [statusMap, setStatusMap] = useState({});
  // Saved people snapshots keyed by id, from the same outreach state. Used to
  // surface HR contacts the user has saved for this company.
  const [savedMap, setSavedMap] = useState({});
  // HR contacts the user has saved whose company matches this job's company.
  // Sourced from the outreach `saved` snapshots (client-kind only).
  const savedHrInCompany = useMemo(() => {
    const target = String(company || "").trim().toLowerCase();
    if (!target) return [];
    return Object.values(savedMap || {}).filter(
      (s) =>
        s &&
        s.kind === "client" &&
        String(s.company || "").trim().toLowerCase() === target,
    );
  }, [savedMap, company]);
  // Active section tab: "description" | "hr" | "candidates".
  const [detailTab, setDetailTab] = useState("description");
  // Reverse-match in-flight flag + how many matches to keep (the server caps
  // this at 100; it ranks everyone either way and only the cut-off changes).
  const [matching, setMatching] = useState(false);
  const [matchCount, setMatchCount] = useState(MATCH_COUNT_DEFAULT);
  // The matches live behind a one-off picker rather than on the page — opening
  // it is a deliberate "show me who fits", and what you take from it becomes a
  // chosen candidate.
  const [matchesOpen, setMatchesOpen] = useState(false);

  const loadMatchedCandidates = useCallback(async () => {
    if (!isAuthenticated || !user?.sub || !id) {
      setMatchedCandidates([]);
      setChosenCandidates([]);
      return;
    }
    setCandLoading(true);
    try {
      const [matchedRes, chosenRes] = await Promise.allSettled([
        fetchWithAuth(
          `/api/jobs/${encodeURIComponent(id)}/candidates?userId=${encodeURIComponent(user.sub)}`,
        ).then((r) => (r.ok ? r.json() : [])),
        fetchWithAuth(
          `/api/jobs/${encodeURIComponent(id)}/chosen-candidates`,
        ).then((r) => (r.ok ? r.json() : [])),
      ]);
      const matched =
        matchedRes.status === "fulfilled" && Array.isArray(matchedRes.value)
          ? matchedRes.value
          : [];
      const chosen =
        chosenRes.status === "fulfilled" && Array.isArray(chosenRes.value)
          ? chosenRes.value
          : [];
      setChosenCandidates(chosen);
      // Shortlisted people stay in the ranked list rather than being filtered out.
      // They earned their rank either way, and the picker marks them as already
      // added — dropping them made them vanish the moment you added them, and
      // again on every re-match after that.
      setMatchedCandidates(matched);
    } catch {
      setMatchedCandidates([]);
      setChosenCandidates([]);
    } finally {
      setCandLoading(false);
    }
  }, [id, user?.sub, isAuthenticated]);

  useEffect(() => {
    loadMatchedCandidates();
  }, [loadMatchedCandidates]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let alive = true;
    // Statuses come from candidate_state (one row per candidate); the
    // encrypted outreach blob is still where `saved` lives.
    Promise.all([getCandidateState(), getOutreachState()])
      .then(([states, s]) => {
        if (!alive) return;
        setStatusMap(states || {});
        setSavedMap(s?.saved || {});
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  // Stage change from the profile modal. Always scoped to this job — that's
  // the only scope status has, and here the job is fixed by context.
  // Optimistic, reverting if the write fails, so the tile behind the modal
  // moves in the same beat as the select.
  const changeCandidateStatus = useCallback(
    async (candidateId, next) => {
      if (!candidateId || !id) return;
      const before = statusMap[candidateId];
      setStatusMap((m) => ({
        ...m,
        [candidateId]: {
          ...(m[candidateId] || {}),
          jobs: { ...(m[candidateId]?.jobs || {}), [id]: next },
        },
      }));
      try {
        const { state } = await patchCandidateStatus(candidateId, {
          jobId: id,
          status: next,
        });
        setStatusMap((m) => ({ ...m, [candidateId]: state }));
      } catch {
        setStatusMap((m) => ({ ...m, [candidateId]: before }));
      }
    },
    [id, statusMap],
  );

  // Reverse match: rank every candidate in the database against this job, then
  // show them. No pool picker — there is one candidate pool. The server queues the
  // work (202 + jobId) and we poll it to its end; the activity tray shows it too.
  const matchAllCandidates = useCallback(async () => {
    if (!user?.sub || !id) return;
    setMatching(true);
    try {
      const r = await fetchWithAuth(
        `/api/jobs/${encodeURIComponent(id)}/match-candidates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.sub, top_k: matchCount }),
        },
      );
      const started = await r.json().catch(() => null);
      if (!r.ok || !started?.jobId) throw new Error("match failed");
      const deadline = Date.now() + REVERSE_MATCH_MAX_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REVERSE_MATCH_POLL_MS));
        let status = null;
        try {
          const sr = await fetchWithAuth(
            `/api/candidatematch/status?jobId=${encodeURIComponent(started.jobId)}`,
          );
          status = await sr.json().catch(() => null);
        } catch {
          continue; // transient blip — keep polling until the ceiling
        }
        if (status?.status === "succeeded" || status?.status === "failed") break;
      }
      await loadMatchedCandidates();
      setDetailTab("candidates");
    } catch {
      // best-effort; the tab will just show the prior state
    } finally {
      setMatching(false);
    }
  }, [id, user?.sub, matchCount, loadMatchedCandidates]);

  // Attach the picks from the matches modal to this job. One bulk call — a
  // hundred single assigns would eat the whole rate-limit window on one click.
  const addChosenCandidates = useCallback(
    async (picks) => {
      const r = await fetchWithAuth(
        `/api/jobs/${encodeURIComponent(id)}/chosen-candidates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidates: picks.map((c) => ({
              id: c._id?.$oid || c._id || c.id || "",
              origin: c.origin === "external" ? "external" : "internal",
            })),
          }),
        },
      );
      if (!r.ok) throw new Error("attach failed");
      await loadMatchedCandidates();
      // The picker opens from the header action row, so the user may not be on
      // the tab their new candidates just landed on — take them there.
      setDetailTab("candidates");
    },
    [id, loadMatchedCandidates],
  );

  // Who is already on this job. The picker reads it to mark those rows as
  // added — including ones a fresh match run surfaces again.
  const chosenKeys = useMemo(
    () =>
      new Set(
        chosenCandidates.map((c) => String(c._id?.$oid || c._id || c.id || "")),
      ),
    [chosenCandidates],
  );

  // Sits in the header action row beside "Source candidates". Hosts that embed
  // the view read-only (a preview) pass no onSourceCandidates and so
  // can't run a match — but they can still open the picker on matches that
  // already exist. Auth-gated because the picker itself is.
  const showMatchesButton = !descriptionOnly && jobMatchOn &&
    isAuthenticated && (!!onSourceCandidates || matchedCandidates.length > 0);

  // Shared "Top N" picker — same control in the header and the empty state.
  const matchCountPicker = (
    <MatchCountSelect
      value={matchCount}
      onChange={(e) => setMatchCount(Number(e.target.value))}
      disabled={matching}
      aria-label="Number of candidates to match"
      title="How many matches to keep"
    >
      {MATCH_COUNT_OPTIONS.map((n) => (
        <option key={n} value={n}>
          Top {n}
        </option>
      ))}
    </MatchCountSelect>
  );

  // Stage groups in pipeline order: Target at the top through to Rejected at
  // the bottom, the same direction the stage picker lists them, so the page
  // reads as the funnel itself. Empty stages are skipped — nine headings for
  // four people is noise.
  const shortlistGroups = useMemo(() => {
    const order = STATUSES;
    const byStage = new Map();
    for (const c of chosenCandidates) {
      const st = statusFor(statusMap, id, candidateKey(c));
      if (!byStage.has(st)) byStage.set(st, []);
      byStage.get(st).push(c);
    }
    const pct = (c) =>
      typeof c.similarity_score === "number" ? c.similarity_score : -1;
    return order
      .filter((s) => byStage.has(s.id))
      .map((s) => ({
        ...s,
        // Best fit first inside a stage; name breaks ties so the order is
        // stable between renders.
        rows: byStage
          .get(s.id)
          .slice()
          .sort(
            (a, b) =>
              pct(b) - pct(a) ||
              String(a.name || "").localeCompare(String(b.name || "")),
          ),
      }));
  }, [chosenCandidates, statusMap, id]);

  // One shortlist row. The stage lives on the group heading above it, so the
  // row carries only what distinguishes this person from the next.
  const renderCandidateRow = (c, i, rail) => {
    const name = c.name || c.full_name || c.title || "—";
    const titleText = c.position || c.role || c.title || "";
    const company = c.company || "";
    const cLoc = formatLocation(c) || formatCountry(c) || "";
    const candId = candidateKey(c);
    // Stamped by the server on hydrate. Absent on anything cached from before
    // that existed — omit the mark rather than guess a store.
    const origin =
      c.origin === "external" || c.origin === "internal" ? c.origin : null;
    const score =
      typeof c.similarity_score === "number"
        ? Math.round(c.similarity_score * 100)
        : null;
    // Opens the profile modal over the job — where the summary, experience,
    // contact links and how they got here now live.
    const openCandidate = () => {
      if (!candId) return;
      setProfilePerson({ person: c, kind: "candidate" });
    };
    return (
      <PersonRow
        key={candId || i}
        $rail={rail}
        role="button"
        tabIndex={0}
        onClick={openCandidate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openCandidate();
          }
        }}
      >
        <span className="r-av">
          <ContactAvatar
            src={c.thumbnail_src || c.photo || ""}
            name={name}
            tint={avatarTint(name)}
            size={34}
          />
        </span>
        <span className="r-who">
          <span className="l-1">
            <span className="n-text">{name}</span>
            {origin && <OriginMark origin={origin} />}
          </span>
          <span className="l-2" style={{ display: "block" }}>
            {titleText || "Title not on file"}
          </span>
        </span>
        <span className="r-where">
          <span className="l-1" style={{ display: "block" }}>
            {company || "—"}
          </span>
          {cLoc && (
            <span className="l-2" style={{ display: "block" }}>
              <FiMapPin size={11} />
              {cLoc}
            </span>
          )}
        </span>
        <span className={`r-score${score === null ? " is-none" : ""}`}>
          {score === null ? "—" : `${score}%`}
        </span>
      </PersonRow>
    );
  };

  // One HR contact row. Same shape as a shortlist row so the two lists read as
  // one page — minus the stage rail and match, which a contact doesn't have.
  // The right cell says whether we hold an address: the thing that decides
  // whether you can act on them at all.
  const renderContactRow = (c, i) => {
    const name = c.name || c.full_name || "—";
    const titleText = c.title || c.role || c.position || "";
    const org = c.company || c.org || "";
    // Every contact in these lists works at this company by construction, so
    // printing it on each row is a column of the same word. Where that's the
    // case the location takes the lead line instead.
    const sameCompany =
      !!org &&
      org.trim().toLowerCase() === String(company || "").trim().toLowerCase();
    const cLoc =
      formatLocation(c) || formatCountry(c) || c.country || c.location || "";
    const contactId = candidateKey(c);
    const email = c.email || c.probable_email || "";
    const openContact = () => setProfilePerson({ person: c, kind: "contact" });
    return (
      <PersonRow
        key={contactId || i}
        $rail="var(--line-strong)"
        role="button"
        tabIndex={0}
        onClick={openContact}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openContact();
          }
        }}
      >
        <span className="r-av">
          <ContactAvatar
            src={c.avatarSrc || c.thumbnail_src || c.photo || ""}
            name={name}
            tint={avatarTint(name)}
            size={34}
          />
        </span>
        <span className="r-who">
          <span className="l-1">
            <span className="n-text">{name}</span>
          </span>
          <span className="l-2" style={{ display: "block" }}>
            {titleText || "Title not on file"}
          </span>
        </span>
        <span className="r-where">
          {sameCompany ? (
            <span className="l-1" style={{ display: "block" }}>
              {cLoc ? (
                <>
                  <FiMapPin size={11} />
                  {cLoc}
                </>
              ) : (
                "—"
              )}
            </span>
          ) : (
            <>
              <span className="l-1" style={{ display: "block" }}>
                {org || "—"}
              </span>
              {cLoc && (
                <span className="l-2" style={{ display: "block" }}>
                  <FiMapPin size={11} />
                  {cLoc}
                </span>
              )}
            </>
          )}
        </span>
        {email && (
          <span className="r-mail" title={`Email on file — ${email}`}>
            <FiMail size={13} />
          </span>
        )}
      </PersonRow>
    );
  };

  return (
    <DetailWrap>
      <Btn $ghost $sm onClick={onBack} style={{ marginBottom: 14 }}>
        <FiChevronLeft size={14} /> Back to {backLabel}
      </Btn>

      <JDCard>
        <JDHeadTop>
          <CompanyLogo name={company} size={56} radius={8} bg={companyColor(company)} fallback={initialOf(company)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <JDCompanyRow>
              <span className="name">{company || "—"}</span>
              {jobFunction && (
                <>
                  <span className="sep">·</span>
                  <span className="type">{jobFunction}</span>
                </>
              )}
              {id && <JDCode>#{id.slice(-8)}</JDCode>}
            </JDCompanyRow>
            <JDTitle>{title}</JDTitle>
            <JDMeta>
              <span
                ref={locItemRef}
                className={`item${hasMultiLoc ? " clickable" : ""}`}
                onClick={(e) => {
                  if (!hasMultiLoc) return;
                  e.stopPropagation();
                  setShowLocs((v) => !v);
                }}
                title={hasMultiLoc ? "Show all locations" : undefined}
              >
                <FiMapPin size={12} /> {loc}
                {showLocs && hasMultiLoc && (
                  <LocPopover onClick={(e) => e.stopPropagation()}>
                    <LocPopoverScroll>
                      <div className="lp-head">
                        {allLocs.length} locations
                      </div>
                      {allLocs.map((l, i) => (
                        <div key={i} className="lp-row">
                          {l}
                        </div>
                      ))}
                    </LocPopoverScroll>
                  </LocPopover>
                )}
              </span>
              {(full || rel) && (
                <span className="item">
                  <FiClock size={12} /> Posted {full}
                  {full && rel ? (
                    <>
                      <span style={{ color: "var(--ink-faint)" }}>·</span> {rel}
                    </>
                  ) : (
                    rel
                  )}
                </span>
              )}
            </JDMeta>
            <JDPills>
              {jobFunction && <Tag>{jobFunction}</Tag>}
              {seniority && <Tag>{seniority}</Tag>}
            </JDPills>
          </div>
          {onToggleSave && (
            <SaveStar
              type="button"
              $on={saved}
              aria-pressed={saved}
              aria-label={saved ? "Remove from saved" : "Save this role"}
              title={saved ? "Saved — click to remove" : "Save this role"}
              onClick={() => onToggleSave(job)}
            >
              {saved ? <StarFilled size={20} /> : <FiStar size={20} />}
            </SaveStar>
          )}
        </JDHeadTop>
        {(onSourceCandidates || url || showMatchesButton) && (
          <JDActions>
            {onSourceCandidates && (
              <Btn $primary $lg onClick={onSourceCandidates}>
                <Spark size={14} /> Source candidates
              </Btn>
            )}
            {/* No count on this label: it landed a beat after the button and
                shoved the actions beside it sideways, and the number was how
                many had been ranked, not how many are worth looking at. The
                picker inside the modal says how many it ranks. */}
            {showMatchesButton && (
              <Btn $accent $lg onClick={() => setMatchesOpen(true)}>
                <Spark size={14} /> Match candidates
              </Btn>
            )}
            {url && (
              <BtnLink href={url} target="_blank" rel="noreferrer" $lg>
                <FiExternalLink size={12} /> Open job posting
              </BtnLink>
            )}
          </JDActions>
        )}
      </JDCard>

      {job.closed && (
        <JDClosedBanner>
          <FiAlertCircle size={14} />
          <div>
            <b>This posting has closed.</b> It is no longer listed by the
            company. Your candidates, HR contacts and notes are kept here so you
            can pick the work back up.
          </div>
        </JDClosedBanner>
      )}

      <JDGlanceStrip $count={4 + (country ? 1 : 0) + (seniority ? 1 : 0)}>
        <JDGlanceCell>
          <JDGlanceLbl>Job code</JDGlanceLbl>
          <JDGlanceVal style={{ fontFamily: "Geist Mono, monospace" }}>
            #{id ? id.slice(-8) : "—"}
          </JDGlanceVal>
        </JDGlanceCell>
        {country && (
          <JDGlanceCell>
            <JDGlanceLbl>Country</JDGlanceLbl>
            <JDGlanceVal>{country}</JDGlanceVal>
          </JDGlanceCell>
        )}
        <JDGlanceCell>
          <JDGlanceLbl>Function</JDGlanceLbl>
          <JDGlanceVal>{jobFunction || "—"}</JDGlanceVal>
        </JDGlanceCell>
        {seniority && (
          <JDGlanceCell>
            <JDGlanceLbl>Seniority</JDGlanceLbl>
            <JDGlanceVal>{seniority}</JDGlanceVal>
          </JDGlanceCell>
        )}
        <JDGlanceCell>
          <JDGlanceLbl>Posted</JDGlanceLbl>
          <JDGlanceVal>{rel || "—"}</JDGlanceVal>
        </JDGlanceCell>
        <JDGlanceCell>
          <JDGlanceLbl>Company</JDGlanceLbl>
          <JDGlanceVal>{company || "—"}</JDGlanceVal>
        </JDGlanceCell>
      </JDGlanceStrip>

      <Tabs style={{ marginTop: 4 }}>
        <Tab
          type="button"
          $active={detailTab === "description"}
          onClick={() => setDetailTab("description")}
        >
          Description
        </Tab>
        {!descriptionOnly && (
          <Tab
            type="button"
            $active={detailTab === "hr"}
            onClick={() => setDetailTab("hr")}
          >
            HR contacts
            {hrContacts.length > 0 && <TabCount>{hrContacts.length}</TabCount>}
          </Tab>
        )}
        {isAuthenticated && !descriptionOnly && (
          <Tab
            type="button"
            $active={detailTab === "candidates"}
            onClick={() => setDetailTab("candidates")}
          >
            Candidates
            {chosenCandidates.length > 0 && (
              <TabCount>{chosenCandidates.length}</TabCount>
            )}
          </Tab>
        )}
      </Tabs>

      {detailTab === "description" && (
      <JDDescription>
        <JDSectionH>About the role</JDSectionH>
        {job.description ? (
          <JobDescriptionHtml html={job.description} />
        ) : (
          <p style={{ color: "var(--ink-muted)" }}>
            No description provided for this role.
          </p>
        )}
      </JDDescription>
      )}

      {detailTab === "hr" && (
      <>
      {savedHrInCompany.length > 0 && (
        <JDCard style={{ marginBottom: 14 }}>
          <JDContactsHead>
            <div>
              <JDSectionH style={{ marginBottom: 4 }}>
                Saved HR in this company
              </JDSectionH>
              <div className="sub">
                {savedHrInCompany.length} saved contact
                {savedHrInCompany.length === 1 ? "" : "s"}
              </div>
            </div>
            <AiPill>
              <StarFilled size={12} /> Saved
            </AiPill>
          </JDContactsHead>
          {savedHrInCompany.map(renderContactRow)}
        </JDCard>
      )}
      <JDCard>
        <JDContactsHead>
          <div>
            <JDSectionH style={{ marginBottom: 4 }}>
              HR contacts at {company || "this company"}
            </JDSectionH>
            {(hrLoading || hrContacts.length > 0) && (
              <div className="sub">
                {hrLoading
                  ? "Looking up mapped contacts…"
                  : `${hrContacts.length} mapped contacts — ready to reach out`}
              </div>
            )}
          </div>
          <AiPill>
            <Spark size={12} /> AI-mapped
          </AiPill>
        </JDContactsHead>
        {!hrLoading && hrContacts.length === 0 && hrFindState === "queued" && (
          <JDEmpty>
            <div className="empty-ico">
              <Spark size={24} />
            </div>
            <div className="empty-title">Searching for HR contacts…</div>
            <div className="empty-text">
              Mapping HR contacts at {company || "this company"}
              {!hrWorldwide && getCountryValues(job)[0]
                ? ` in ${getCountryValues(job)[0]}`
                : " worldwide"}{" "}
              — this can take a couple of minutes. They&rsquo;ll appear here as
              soon as they&rsquo;re found.
            </div>
          </JDEmpty>
        )}
        {!hrLoading && hrContacts.length === 0 && hrFindState === "empty" && (
          <JDEmpty>
            <div className="empty-ico">
              <FiUsers size={24} />
            </div>
            <div className="empty-title">Search finished — nobody found</div>
            <div className="empty-text">
              {hrWorldwide || !getCountryValues(job)[0] ? (
                <>
                  No current HR contacts were found for{" "}
                  {company || "this company"} anywhere.
                </>
              ) : (
                <>
                  No current HR contacts were found at{" "}
                  {company || "this company"} in {getCountryValues(job)[0]}.
                  Their HR team may sit in another country.
                </>
              )}
            </div>
            <div
              className="empty-cta"
              style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}
            >
              {!hrWorldwide && getCountryValues(job)[0] && (
                <Btn $primary onClick={() => startHrFind(true)}>
                  <Spark size={14} /> Search worldwide instead
                </Btn>
              )}
              <Btn
                onClick={() =>
                  navigate(
                    company
                      ? `/outreach?tab=clients&company=${encodeURIComponent(company)}`
                      : "/outreach?tab=clients",
                  )
                }
              >
                <FiUsers size={14} /> See all HR{company ? ` for ${company}` : ""}
              </Btn>
            </div>
          </JDEmpty>
        )}
        {!hrLoading &&
          hrContacts.length === 0 &&
          hrFindState !== "queued" &&
          hrFindState !== "empty" && (
          <JDEmpty>
            <div className="empty-ico">
              <FiUsers size={24} />
            </div>
            <div className="empty-title">No HR contacts mapped yet</div>
            <div className="empty-text">
              {hrFindState !== "idle" ? (
                hrFindState
              ) : (
                <>
                  We don&rsquo;t have any HR contacts mapped for this location
                  yet. Search for them by company and country, or see the ones
                  you already saved in Outreach.
                </>
              )}
            </div>
            <div
              className="empty-cta"
              style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}
            >
              {company && (
                <Btn $primary onClick={() => startHrFind(false)}>
                  <Spark size={14} /> Find HR contacts
                </Btn>
              )}
              <Btn
                onClick={() =>
                  navigate(
                    company
                      ? `/outreach?tab=clients&company=${encodeURIComponent(company)}`
                      : "/outreach?tab=clients",
                  )
                }
              >
                <FiUsers size={14} /> See all HR{company ? ` for ${company}` : ""}
              </Btn>
            </div>
          </JDEmpty>
        )}
        {hrContacts.length > 0 && hrContacts.map(renderContactRow)}
      </JDCard>
      </>
      )}

      {isAuthenticated && detailTab === "candidates" && (
        <JDCard style={{ marginBottom: 14 }}>
          <JDContactsHead>
            <div>
              <JDSectionH style={{ marginBottom: 4 }}>Shortlist</JDSectionH>
              {candLoading && (
                <div className="sub">Loading candidates for this role…</div>
              )}
            </div>
          </JDContactsHead>
          {!candLoading && chosenCandidates.length === 0 && (
            <JDEmpty>
              <div className="empty-ico">
                <FiUsers size={24} />
              </div>
              <div className="empty-title">Nobody shortlisted yet</div>
              <div className="empty-text">
                {showMatchesButton
                  ? "Let AI rank your talent pool against this role, or pick people from Outreach — either way they land here, ready to move through this role."
                  : "Pick people from Outreach and they land here, ready to move through this role."}
              </div>
              <div
                className="empty-cta"
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                {showMatchesButton && (
                  <Btn $accent onClick={() => setMatchesOpen(true)}>
                    <Spark size={14} /> Match candidates
                  </Btn>
                )}
                <Btn onClick={() => navigate("/outreach")}>
                  <FiUsers size={14} /> Go to Outreach
                </Btn>
              </div>
            </JDEmpty>
          )}
          {chosenCandidates.length > 0 &&
            shortlistGroups.map((g) => {
              const rail = stageColor(g.id);
              return (
                <StageGroup key={g.id} $rail={rail}>
                  <div className="g-h">
                    <i className="g-dot" />
                    {g.label}
                    <span className="g-count">{g.rows.length}</span>
                    <span className="g-rule" />
                  </div>
                  {g.rows.map((c, i) => renderCandidateRow(c, i, rail))}
                </StageGroup>
              );
            })}
        </JDCard>
      )}

      {isAuthenticated && profilePerson && (
        <PersonProfileModal
          person={profilePerson.person}
          kind={profilePerson.kind}
          jobTitle={title}
          companyName={company}
          status={statusFor(
            statusMap,
            id,
            candidateKey(profilePerson.person),
          )}
          onChangeStatus={(next) =>
            changeCandidateStatus(candidateKey(profilePerson.person), next)
          }
          // The one trip still worth taking: composing and the interaction
          // timeline live there. Candidates and contacts sit on different tabs.
          onOpenInOutreach={() => {
            const key = candidateKey(profilePerson.person);
            const tab =
              profilePerson.kind === "candidate" ? "candidates" : "clients";
            navigate(
              `/outreach?tab=${tab}${
                key ? `&personId=${encodeURIComponent(key)}` : ""
              }`,
            );
          }}
          onClose={() => setProfilePerson(null)}
        />
      )}

      {isAuthenticated && matchesOpen && (
        <AiMatchesModal
          onClose={() => setMatchesOpen(false)}
          candidates={matchedCandidates}
          loading={candLoading}
          matching={matching}
          matchCountPicker={matchCountPicker}
          onMatch={matchAllCandidates}
          canMatch={!!onSourceCandidates}
          jobId={id}
          statusMap={statusMap}
          chosenKeys={chosenKeys}
          onAdd={addChosenCandidates}
        />
      )}

    </DetailWrap>
  );
}
